import { useNavigate } from 'react-router-dom'

import { F0Button } from '@factorialco/f0-react'
import { InAppCommsButton } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'

import {
  isValidFeature,
  isValidModule,
  isValidReason,
} from 'modules/in_app_comms/lib/enumValidation'
import useCreateUpsellRequest from 'modules/upsell/hooks/useCreateUpsellRequest'

import UpsellButton from './_UpsellButton'

interface ButtonProps {
  button: InAppCommsButton
  variant: string
  size: string
  componentType: string
  componentName: string
  onClick?: () => void
  extraEventProps?: Record<string, string>
  onUpsellSuccess?: () => void
  onUpsellError?: () => void
  upsellRequestComponentType?: string
}

const CommsButton = ({
  button,
  onClick,
  variant,
  size,
  componentType,
  componentName,
  extraEventProps,
  onUpsellSuccess,
  onUpsellError,
  upsellRequestComponentType,
}: ButtonProps) => {
  const { create } = useCreateUpsellRequest()
  const navigate = useNavigate()
  const { email, company } = useCurrentSession()

  const triggerEvent = (action: string) => {
    track(trackingEvents.inAppComms.clickInAppCommsComponent, {
      componentType,
      componentName,
      buttonType: button.type,
      buttonAction: action,
      ...extraEventProps,
    })
  }

  const processLink = (link: string | null | undefined): string => {
    if (!link) return ''
    let processedLink = link
    if (email) {
      processedLink = processedLink.replace(/\{\{email\}\}/g, email)
    }
    if (company?.id) {
      processedLink = processedLink.replace(/\{\{company_id\}\}/g, String(company.id))
    }
    return processedLink
  }

  const extractPathname = (url: string): string => {
    if (!isAbsoluteURL(url)) {
      return url
    }
    try {
      const urlObj = new URL(url)
      return urlObj.pathname + urlObj.search + urlObj.hash
    } catch {
      return url
    }
  }

  if (!button) return null
  switch (button.type) {
    case 'CommsLinkButtonRecord':
      return (
        <F0Button
          label={button.text}
          onClick={() => {
            const processedLink = processLink(button.link)
            triggerEvent(`Open link: ${button.link}`)
            if (button.isInternalLink) {
              const pathname = extractPathname(processedLink)
              if (button.openInNewTab) {
                window.open(pathname, '_blank')
              } else {
                navigate(pathname)
              }
            } else {
              if (button.openInNewTab) {
                window.open(processedLink, '_blank')
              } else {
                window.location.href = processedLink
              }
            }
            onClick?.()
          }}
          variant={variant}
          size={size}
        />
      )

    case 'CommsCloseButtonRecord':
      return (
        <F0Button
          label={button.text}
          onClick={() => {
            triggerEvent('Close component')
            onClick?.()
          }}
          variant={variant}
          size={size}
        />
      )

    case 'CommsUpsellButtonRecord':
      return (
        <UpsellButton
          size={size}
          label={button.text}
          onClick={async () => {
            const upsellProps = {
              moduleRequested: isValidModule(button.moduleRequested)
                ? button.moduleRequested
                : undefined,
              requestSource: button.requestSource || undefined,
              requestReason: isValidReason(button.requestReason) ? button.requestReason : undefined,
              featureRequested: isValidFeature(button.featureRequested)
                ? button.featureRequested
                : undefined,
              moreInfo: button.moreInfoUrl ?? undefined,
              upsellRequestComponentType: upsellRequestComponentType || 'in_app_comms_button',
            }

            const result = await create(upsellProps)
            const handled = handleResult(result, () => undefined)

            if (handled.success) {
              onUpsellSuccess?.()
            } else {
              onUpsellError?.()
            }

            track(trackingEvents.inAppComms.clickInAppCommsComponent, {
              componentType,
              componentName,
              buttonType: button.type,
              buttonAction: 'Upsell request',
              ...upsellProps,
            })
            onClick?.()
          }}
        />
      )

    default:
      return (
        <F0Button
          label={button.text}
          onClick={() => {
            triggerEvent('Default on click')
            onClick?.()
          }}
          variant={variant}
          size={size}
        />
      )
  }
}

export default CommsButton
